import React, { useState, useEffect } from 'react';
import '../style/component/PaymentModal.css';

function PaymentModal({ isOpen, onClose, itemName, itemPrice, isLoggedIn, onLogin }) {
  const [showLogin, setShowLogin] = useState(!isLoggedIn);
  const [formData, setFormData] = useState({
    cardholderName: '',
    email: '',
    streamId: '',
    gamingId: '',
    cardNumber: '',
    expiry: '',
    cvv: ''
  });

  const [loginData, setLoginData] = useState({
    email: '',
    password: ''
  });

  const [isProcessing, setIsProcessing] = useState(false);
  const [paymentSuccess, setPaymentSuccess] = useState(false);
  const [errors, setErrors] = useState({}); // <-- validation errors

  useEffect(() => {
    // reset login view when modal opens/closes
    if (!isOpen) {
      setShowLogin(!isLoggedIn);
      setIsProcessing(false);
      setPaymentSuccess(false);
      setErrors({});
      setFormData({
        cardholderName: '',
        email: '',
        streamId: '',
        gamingId: '',
        cardNumber: '',
        expiry: '',
        cvv: ''
      });
    }
  }, [isOpen, isLoggedIn]);

  useEffect(() => {
    let t;
    if (paymentSuccess) {
      t = setTimeout(() => {
        // close modal after success animation
        setPaymentSuccess(false);
        onClose();
        // reset form
        setFormData({
          cardholderName: '',
          email: '',
          streamId: '',
          gamingId: '',
          cardNumber: '',
          expiry: '',
          cvv: ''
        });
        setErrors({});
      }, 1800);
    }
    return () => clearTimeout(t);
  }, [paymentSuccess, onClose]);

  const handleInputChange = (e) => {
    const { name, value } = e.target;
    setFormData(prev => ({
      ...prev,
      [name]: value
    }));
    // remove field error on change
    setErrors(prev => {
      const copy = { ...prev };
      delete copy[name];
      return copy;
    });
  };

  const handleLoginChange = (e) => {
    const { name, value } = e.target;
    setLoginData(prev => ({
      ...prev,
      [name]: value
    }));
  };

  const handleLoginSubmit = () => {
    if (loginData.email && loginData.password) {
      onLogin && onLogin(loginData);
      setShowLogin(false);
    }
  };

  const validate = () => {
    const errs = {};
    const emailRe = /^[^\s@]+@[^\s@]+\.[^\s@]+$/;
    if (!formData.cardholderName.trim()) errs.cardholderName = 'Cardholder name is required';
    if (!formData.email.trim()) errs.email = 'Email is required';
    else if (!emailRe.test(formData.email)) errs.email = 'Enter a valid email';

    
  // New Stream ID validation
  if (!formData.streamId.trim()) {
    errs.streamId = 'Stream ID is required';
  } else if (!/^[A-Za-z0-9]{6,12}$/.test(formData.streamId)) {
    errs.streamId = 'Stream ID must be 6-12 alphanumeric characters';
  }

  // New Gaming ID validation
  if (!formData.gamingId.trim()) {
    errs.gamingId = 'Gaming ID is required';
  } else if (!/^[A-Za-z0-9]{6,12}$/.test(formData.gamingId)) {
    errs.gamingId = 'Gaming ID must be 6-12 alphanumeric characters';
  }

    if (!formData.cardNumber.trim()) errs.cardNumber = 'Card number is required';
    else if (!/^\d{12,19}$/.test(formData.cardNumber.replace(/\s+/g, ''))) errs.cardNumber = 'Enter a valid card number (12-19 digits)';
    // if (!formData.expiry.trim()) errs.expiry = 'Expiry is required';
    // else if (!/^(0[1-9]|1[0-2])\/?\d{2}$/.test(formData.expiry)) errs.expiry = 'Expiry must be MM/YY';

      if (!formData.expiry.trim()) {
    errs.expiry = 'Expiry is required';
  } else if (!/^(0[1-9]|1[0-2])\/?\d{2}$/.test(formData.expiry)) {
    errs.expiry = 'Expiry must be MM/YY';
  } else {
    // Check if expiry date is not in the past
    const [month, year] = formData.expiry.split('/');
    const expiry = new Date(2000 + parseInt(year), parseInt(month) - 1);
    const today = new Date();
    if (expiry < new Date(today.getFullYear(), today.getMonth())) {
      errs.expiry = 'Card has expired';
    }
  }
    if (!formData.cvv.trim()) errs.cvv = 'CVV is required';
    else if (!/^\d{3,4}$/.test(formData.cvv)) errs.cvv = 'CVV must be 3 or 4 digits';
    return errs;
  };

  const handlePayment = () => {
    const validation = validate();
    if (Object.keys(validation).length > 0) {
      setErrors(validation);
      // flash modal border and focus first invalid field
      const modal = document.querySelector('.modal-content');
      if (modal) {
        modal.classList.add('pm-invalid');
        setTimeout(() => modal.classList.remove('pm-invalid'), 700);
      }
      const firstKey = Object.keys(validation)[0];
      const el = document.querySelector(`input[name="${firstKey}"]`);
      if (el) el.focus();
      return;
    }

    setIsProcessing(true);

    // simulate payment processing then show animated success
    setTimeout(() => {
      setIsProcessing(false);
      setPaymentSuccess(true);
    }, 900);
  };

  if (!isOpen) return null;

  // show first error message on top (if any)
  const topError = Object.values(errors)[0] || '';

  return (
    <div className="modal-overlay" onClick={onClose}>
      <div className="modal-content" onClick={(e) => e.stopPropagation()}>
        {paymentSuccess ? (
          <div className="pm-success">
            <svg className="pm-check" viewBox="0 0 52 52" aria-hidden>
              <circle className="pm-check-circle" cx="26" cy="26" r="25" fill="none"/>
              <path className="pm-check-mark" fill="none" d="M14 27l7 7 16-16" />
            </svg>
            <div className="pm-success-text">Payment successful</div>
            <div className="pm-subtext">Thank you for purchasing {itemName}</div>
          </div>
        ) : showLogin ? (
          <div className="login-container">
            <h2 className="modal-title">Login Required</h2>
            <p className="login-subtitle">Please login to continue with your purchase</p>

            <div className="login-form">
              <div className="form-group">
                <label>Email</label>
                <input
                  type="email"
                  name="email"
                  placeholder="Enter your email"
                  value={loginData.email}
                  onChange={handleLoginChange}
                  className="form-input"
                />
              </div>

              <div className="form-group">
                <label>Password</label>
                <input
                  type="password"
                  name="password"
                  placeholder="Enter your password"
                  value={loginData.password}
                  onChange={handleLoginChange}
                  className="form-input"
                />
              </div>

              <div className="button-group">
                <button type="button" onClick={onClose} className="cancel-btn" disabled={isProcessing}>
                  Cancel
                </button>
                <button type="button" onClick={handleLoginSubmit} className="login-btn" disabled={isProcessing}>
                  Login & Continue
                </button>
              </div>
            </div>
          </div>
        ) : (
          <div className="payment-container">
            <div className="payment-header">
              <h2 className="payment-title">Payment Checkout</h2>
              <div className="payment-info">
                <div className="item-name">{itemName}</div>
                <div className="item-total">Total: ${itemPrice}</div>
              </div>
            </div>

            {/* {topError && <div className="pm-error" role="alert">{topError}</div>} */}

            <div className="payment-form">
              <div className="form-group">
                <label>Cardholder Name</label>
                <input
                  type="text"
                  name="cardholderName"
                  placeholder="Name on card"
                  value={formData.cardholderName}
                  onChange={handleInputChange}
                  className={`form-input ${errors.cardholderName ? 'input-error' : ''}`}
                  disabled={isProcessing}
                />
                {errors.cardholderName && <div className="field-error">{errors.cardholderName}</div>}
              </div>

              <div className="form-group">
                <label>Email</label>
                <input
                  type="email"
                  name="email"
                  placeholder="Your Email ID Here"
                  value={formData.email}
                  onChange={handleInputChange}
                  className={`form-input ${errors.email ? 'input-error' : ''}`}
                  disabled={isProcessing}
                />
                {errors.email && <div className="field-error">{errors.email}</div>}
              </div>

                  <div className="form-group">
            <label>Stream ID</label>
            <input
              type="text"
              name="streamId"
              placeholder="Enter Stream ID"
              value={formData.streamId}
              onChange={handleInputChange}
              className={`form-input ${errors.streamId ? 'input-error' : ''}`}
              disabled={isProcessing}
            />
            {errors.streamId && <div className="field-error">{errors.streamId}</div>}
          </div>

           <div className="form-group">
            <label>Gaming ID</label>
            <input
              type="text"
              name="gamingId"
              placeholder="Enter Gaming ID"
              value={formData.gamingId}
              onChange={handleInputChange}
              className={`form-input ${errors.gamingId ? 'input-error' : ''}`}
              disabled={isProcessing}
            />
            {errors.gamingId && <div className="field-error">{errors.gamingId}</div>}
          </div>
            

              <div className="form-group">
                <label>Card Number</label>
                <input
                  type="text"
                  name="cardNumber"
                  placeholder="XXXX XXXX XXXX XXXX"
                  value={formData.cardNumber}
                  onChange={handleInputChange}
                  maxLength="19"
                  className={`form-input ${errors.cardNumber ? 'input-error' : ''}`}
                  disabled={isProcessing}
                />
                {errors.cardNumber && <div className="field-error">{errors.cardNumber}</div>}
              </div>

              <div className="form-row">
                <div className="form-group">
                  <label>Expiry</label>
                  <input
                    type="text"
                    name="expiry"
                    placeholder="MM/YY"
                    value={formData.expiry}
                    onChange={handleInputChange}
                    maxLength="5"
                    className={`form-input ${errors.expiry ? 'input-error' : ''}`}
                    disabled={isProcessing}
                  />
                  {errors.expiry && <div className="field-error">{errors.expiry}</div>}
                </div>

                <div className="form-group">
                  <label>CVV</label>
                  <input
                    type="password"
                    name="cvv"
                    placeholder="•••"
                    value={formData.cvv}
                    onChange={handleInputChange}
                    maxLength="4"
                    className={`form-input ${errors.cvv ? 'input-error' : ''}`}
                    disabled={isProcessing}
                  />
                  {errors.cvv && <div className="field-error">{errors.cvv}</div>}
                </div>
              </div>
                </div>

              <div className="button-group">
                <button type="button" onClick={onClose} className="cancel-btn" disabled={isProcessing}>
                  Cancel
                </button>
                <button type="button" onClick={handlePayment} className="pay-btn" disabled={isProcessing}>
                  {isProcessing ? 'Processing...' : `Pay $${itemPrice}`}
                </button>
              </div>
            </div>
         
        )}
      </div>
    </div>
  );
}

export default PaymentModal;